import React, { useState, useRef, useEffect } from "react";
import { FormControl, TextField, InputAdornment } from "@mui/material";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import moment from "moment";
import Select from "react-select";
import { getCurrentWeekRange } from "../Utils/UtilityFunctions";

const baseRangeOptions = [
  { label: "Last 3 months", value: "3m" },
  { label: "Last 6 months", value: "6m" },
  { label: "Last 12 months", value: "12m" },
  { label: "Custom", value: "custom" },
];

const DateRangeSelector = ({
  onDateRangeChange,
  isShowDropdown = true,
  oneWeekMode = false,
  showLastMonth = false,
  dropdownOptions = [],
  defaultRange = "",
}) => {
  const dateRangeOptions =
    dropdownOptions.length > 0
      ? dropdownOptions
      : showLastMonth
      ? [{ label: "Last month", value: "1m" }, ...baseRangeOptions]
      : baseRangeOptions;

  const [dateRange, setDateRange] = useState(
    defaultRange
      ? dateRangeOptions.find((option) => option.value === defaultRange)
      : {
          label: "Last 3 months",
          value: "last3months",
        }
  );
  const currentWeekRange = getCurrentWeekRange();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState([
    {
      startDate: oneWeekMode ? currentWeekRange.from : new Date(),
      endDate: oneWeekMode ? currentWeekRange.to : new Date(),
      key: "selection",
    },
  ]);
  const [weekStartDate, setWeekStartDate] = useState(null);
  const [pickerPosition, setPickerPosition] = useState({
    top: 0,
    right: 0,
    openUpward: false,
  });
  const datePickerRef = useRef(null);

  const CALENDAR_HEIGHT = 340;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  const calculatePickerPosition = () => {
    if (!datePickerRef.current) return;
    const triggerRect = datePickerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const openUpward = spaceBelow < CALENDAR_HEIGHT;

    setPickerPosition({
      top: openUpward
        ? triggerRect.top - CALENDAR_HEIGHT - 12
        : triggerRect.bottom + 12,
      right: window.innerWidth - triggerRect.right,
      openUpward,
    });
  };

  const handleDateRangeChange = (val) => {
    setDateRange(val);
    if (val?.value !== "custom") {
      setShowDatePicker(false);
      setWeekStartDate(null);
      setCustomDateRange([
        {
          startDate: oneWeekMode ? currentWeekRange.from : new Date(),
          endDate: oneWeekMode ? currentWeekRange.to : new Date(),
          key: "selection",
        },
      ]);
      if (onDateRangeChange) {
        onDateRangeChange({ type: val?.value });
      }
    }
  };

  const handlePickerOpen = () => {
    if (oneWeekMode) {
      setWeekStartDate(null);
    }
    if (!showDatePicker) {
      calculatePickerPosition();
    }
    setShowDatePicker(!showDatePicker);
  };

  const handleDateChange = (item) => {
    const { startDate, endDate } = item.selection;

    if (oneWeekMode) {
      if (!weekStartDate || startDate.getTime() !== weekStartDate.getTime()) {
        setWeekStartDate(startDate);
      }

      setCustomDateRange([item.selection]);

      if (startDate && endDate && startDate.getTime() !== endDate.getTime()) {
        if (onDateRangeChange) {
          onDateRangeChange({
            type: "custom",
            startDate,
            endDate,
          });
        }
        setTimeout(() => setShowDatePicker(false), 300);
      }
    } else {
      setCustomDateRange([item.selection]);
      if (startDate && endDate && startDate.getTime() !== endDate.getTime()) {
        if (onDateRangeChange) {
          onDateRangeChange({
            type: "custom",
            startDate,
            endDate,
          });
        }
        setTimeout(() => setShowDatePicker(false), 300);
      }
    }
  };

  return (
    <div style={{ height: "45px", position: "relative" }}>
      {isShowDropdown && (
        <div
          className="mb-2 d-flex justify-content-end"
          style={{ height: "36px" }}
        >
          <FormControl size="small" sx={{ width: 160 }}>
            <Select
              className="basic-single"
              value={dateRange}
              onChange={handleDateRangeChange}
              placeholder={" "}
              options={dateRangeOptions}
              isMulti={false}
              closeMenuOnSelect={true}
              components={{
                IndicatorSeparator: () => null,
              }}
              styles={{
                control: (base, state) => ({
                  ...base,
                  height: 36,
                  fontSize: "12px",
                  color: "#0A0A0A",
                  backgroundColor: "#F3F3F5",
                  fontWeight: "400",
                  borderColor: "#00000000",
                  borderRadius: "8px",
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: "#0000002A",
                  },
                  ...(state.isFocused && {
                    borderColor: "#0000002A",
                    boxShadow: "none",
                  }),
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? "#f5f5f5" : "white",
                  color: "black",
                  fontSize: "12px",
                  fontWeight: "normal",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              menuPortalTarget={document.body}
            />
          </FormControl>
        </div>
      )}

      {(dateRange?.value === "custom" || !isShowDropdown) && (
        <div
          className="mb-2 d-flex justify-content-end"
          style={{ height: "36px" }}
        >
          <div ref={datePickerRef} style={{ position: "relative" }}>
            <TextField
              size="small"
              value={
                customDateRange[0].startDate && customDateRange[0].endDate
                  ? `${moment(customDateRange[0].startDate).format(
                      "DD MMM YYYY"
                    )} - ${moment(customDateRange[0].endDate).format(
                      "DD MMM YYYY"
                    )}`
                  : ""
              }
              onClick={handlePickerOpen}
              placeholder="Select date range"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <i
                      className={"ri-calendar-line"}
                      style={{ fontSize: "16px", color: "#0A0A0A" }}
                    ></i>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 210,
                cursor: "pointer",
                "& .MuiOutlinedInput-root": {
                  height: "36px",
                  fontSize: "12px",
                  "& fieldset": {
                    borderColor: "#0000001A",
                    borderRadius: "8px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#0000003A",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#0000003A",
                  },
                },
              }}
            />
            {showDatePicker && (
              <div
                style={{
                  position: "fixed",
                  top: pickerPosition.top,
                  right: pickerPosition.right,
                  zIndex: 9999,
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: "white",
                  border: "1px solid #d1d5db",
                }}
              >
                <style>
                  {`
                    .rdrDefinedRangesWrapper {
                      display: none;
                    }
                    .rdrDateDisplayWrapper {
                      display: none;
                    }
                    .rdrCalendarWrapper {
                      background: white;
                    }
                  `}
                </style>
                <DateRangePicker
                  ranges={customDateRange}
                  onChange={handleDateChange}
                  months={2}
                  direction="horizontal"
                  rangeColors={["#F97316"]}
                  maxDate={
                    oneWeekMode && weekStartDate
                      ? new Date(
                          weekStartDate.getTime() + 6 * 24 * 60 * 60 * 1000
                        )
                      : undefined
                  }
                  disabledDay={
                    oneWeekMode && weekStartDate
                      ? (date) => {
                          const start = new Date(weekStartDate);
                          start.setHours(0, 0, 0, 0);
                          const end = new Date(start);
                          end.setDate(end.getDate() + 6);
                          end.setHours(23, 59, 59, 999);

                          const current = new Date(date);
                          current.setHours(0, 0, 0, 0);

                          return current < start || current > end;
                        }
                      : undefined
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;